import moment, { type Moment } from "moment";
import { type ThunkDispatch } from "redux-thunk";
import { UPLOAD_ERROR_EVENT } from "#/_Bootstrap/GlobalConstants.ts";
import { store } from "#/_Bootstrap/RoutingAndStore.ts";
import { REQUEST_REGISTER } from "#/Actions/General/GeneralActionConstants.ts";
import { changeRegister, updateOutlet, updateRegister } from "#/Actions/General/LocationActions.ts";
import { setSaleKeys } from "#/Actions/SellScreen/SellScreenSaleKeysActions.ts";
import { updateOpenTime, updateSyncPage, updateSyncStatus } from "#/Actions/Synchronisation/SynchronisationActions.ts";
import { userLogout } from "#/Actions/Users/UserActions.ts";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { getLogLevel, LogLevel } from "#/Utilities/Platform/Logging.ts";
import {
    getOption,
    type Options,
    setOption,
    subscribeToOption,
} from "#/Utilities/Platform/Options/Options.ts";
import { offline, online } from "../../Actions/Information/InformationActions";
import PopupManager from "../../Common/Popups/PopupManager";
import { NAVIGATION_USER_UPDATE } from "../../Layout/Navigation/Navigation";
import FontAwesomeSolid from "../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import { runUploadQueue } from "../Upload/UploadAgent";
import { localTimeMismatchWarning } from "./LocalTimeMismatchWarning";
import {
    AGENT_EVENTS,
    FORCE_SYNC_FLAG,
    FORCE_SYNC_FLAG_KEY,
    LOCAL_INITIAL_DATA,
    type LocalInitialDataForPassing,
    POS_FORCE_SYNC,
    SYNC_EVENTS,
} from "./SyncEvents";

const SYNC_AGENT_POPUP_ID = "synchronisation-large";
const SYNCHRONISATION_FINISHED_EVENT = "synchronisationFinished";

const SYNC_FLAG_WARNING_ID = "synchronisation-flag-warning";

let syncFlagWarning = false;

export default class SyncAgent extends BaseAgent {
    protected register: null | string;
    protected outlet: null | string;
    protected globalMode: boolean;
    protected syncing: boolean;
    protected page: false | number;
    protected popup: boolean;
    protected needFullSync: boolean;

    constructor() {
        super(generateWorkerURL("syncworker"), "Shopfront Synchronisation");

        // Bind the listeners
        this.handleMessage = this.handleMessage.bind(this);
        this.handleStoreChange = this.handleStoreChange.bind(this);
        this.handleForceSync = this.handleForceSync.bind(this);

        // Store the current register and outlet
        this.register = null;
        this.outlet = null;
        this.globalMode = false;

        // Store the status of the synchronisation and the page
        this.syncing = false;
        this.page = false;
        this.popup = false;
        this.needFullSync = false;

        // Start the worker
        this.startWorker();

        // Subscribe to register changes
        store.subscribe(this.handleStoreChange);

        // Subscribe to option changes
        this.handleInstanceChange(
            "_instance_id",
            subscribeToOption("_instance_id", this.handleInstanceChange),
            ""
        );

        // Listen for force sync
        if(typeof document !== "undefined") {
            document.addEventListener(POS_FORCE_SYNC, this.handleForceSync);
        }
    }

    protected handleForceSync(e: Partial<CustomEvent>): void {
        void this.performSync(e.detail.full);
    }

    protected handleRequestLocationMessage(): void {
        this.syncing = false;

        const register = getOption("register");
        const outlet = getOption("outlet");
        const global = getOption("global_mode");

        if(register) {
            this.sendMessage(AGENT_EVENTS.REGISTER, register);
        } else if(outlet) {
            this.sendMessage(AGENT_EVENTS.OUTLET, outlet);
        } else if(global) {
            this.globalMode = true;
            this.sendMessage(AGENT_EVENTS.GLOBAL_MODE, true);
        } else {
            store.dispatch(fireEventType(REQUEST_REGISTER));
        }
    }

    protected handleUpdateLocalStorageRequest(data: any): void {
        const newStorage = data;

        for(const i in newStorage) {
            if(!Object.prototype.hasOwnProperty.call(newStorage, i)) continue;

            if(i === "cost_calculation_method") {
                const old = getOption(i);

                if(old !== newStorage[i] && (old === "last" || newStorage[i] === "last")) {
                    // Need to perform a full sync
                    this.needFullSync = true;
                }
            } else if(i === "outlet_use_global_cost") {
                const old = getOption(i);

                if(old !== newStorage[i]) {
                    // Need to perform a full sync
                    this.needFullSync = true;
                }
            } else if(i === "register_current_invoice_number") {
                // Check to see if we already have an invoice number stored
                const old = localStorage.getItem(i);

                if(old !== null) {
                    continue;
                }
            } else if(this.shouldUpdateSaleKeys(i, newStorage)) {
                store.dispatch(setSaleKeys(newStorage[i]));
            }

            setOption(i as keyof Options, newStorage[i]);
        }
    }

    protected handleOpenCloseTimeRequest(data: {
        openTime: string;
        closeTime: string;
    }): void {
        const openTime = data.openTime;
        let closeTime: string | Moment = data.closeTime;

        let localOpenTime: string | Moment = getOption("register_open_time");

        if(localOpenTime === "" || openTime !== "") {
            store.dispatch(updateOpenTime(data.openTime));
        } else {
            localOpenTime = moment(localOpenTime);
            closeTime = moment(closeTime);

            if(localOpenTime.isBefore(closeTime)) {
                store.dispatch(updateOpenTime(""));
            }
        }
    }

    protected handleRequestInitialDataRequest(): void {
        const initialData = {} as LocalInitialDataForPassing;

        for(const key of LOCAL_INITIAL_DATA) {
            (initialData[key] as Options[typeof key]) = getOption(key);
        }

        this.sendMessage(AGENT_EVENTS.INITIAL_DATA, JSON.stringify(initialData));
    }

    protected handleMessage(data: {
        type: SYNC_EVENTS;
        message: any;
    }): void {
        if(!data.type) {
            console.error("Unknown data passed to SyncAgent", data);

            return;
        }

        switch(data.type) {
            case SYNC_EVENTS.SYNC_START:
                store.dispatch(updateSyncStatus("running"));
                this.syncing = true;
                this.needFullSync = false;
                break;
            case SYNC_EVENTS.SYNC_END:
                if(data.message) {
                    syncFlagWarning = false;

                    PopupManager.remove(FORCE_SYNC_FLAG_KEY);

                    setOption("lastsync", data.message.lastSync);

                    if(typeof data.message.flag !== "undefined") {
                        setOption(FORCE_SYNC_FLAG_KEY, data.message.flag);
                    }
                }

                store.dispatch(updateSyncStatus("stopped"));
                this.syncing = false;

                // Broadcast the end of the sync
                if(typeof document !== "undefined" && this.page !== false) {
                    document.dispatchEvent(new CustomEvent(SYNCHRONISATION_FINISHED_EVENT, {
                        detail: {
                            updated: data.message.updated,
                        },
                    }));

                    document.dispatchEvent(new Event(NAVIGATION_USER_UPDATE));
                }

                this.page = false;

                if(this.needFullSync) {
                    this.handleForceSync({
                        detail: {
                            full: true,
                        },
                    });
                }

                break;
            case SYNC_EVENTS.SYNC_PAGE:
                if(data.message < 2) { // Update on the first and second page
                    document.dispatchEvent(new Event(NAVIGATION_USER_UPDATE));
                }

                store.dispatch(updateSyncPage(data.message));
                this.page = data.message;
                break;
            case SYNC_EVENTS.SYNC_ERROR:
                store.dispatch(updateSyncStatus("error"));
                this.syncing = false;
                break;
            case SYNC_EVENTS.REQUEST_LOCATION:
                this.handleRequestLocationMessage();

                break;
            case SYNC_EVENTS.INVALIDATE_REGISTER:
                this.syncing = false;
                store.dispatch(fireEventType(REQUEST_REGISTER));
                break;
            case SYNC_EVENTS.UPDATE_LOCALSTORAGE:
                this.handleUpdateLocalStorageRequest(data.message);
                break;
            case SYNC_EVENTS.REQUEST_LAST_SYNC:
                this.sendMessage(AGENT_EVENTS.LAST_SYNC, getOption("lastsync"));
                break;
            case SYNC_EVENTS.UPDATE_UTC_OFFSET:
                setOption("utc_offset", data.message.offset);
                localTimeMismatchWarning(data.message.offset, data.message.localTime, data.message.serverTime);
                break;
            case SYNC_EVENTS.OPEN_CLOSE_TIME:
                this.handleOpenCloseTimeRequest(data.message);
                break;
            case SYNC_EVENTS.REQUEST_INITIAL_DATA:
                this.handleRequestInitialDataRequest();
                break;
            case SYNC_EVENTS.TAKINGS_CHANGE:
                sessionStorage.removeItem(data.message);
                break;
            case SYNC_EVENTS.UPDATE_REGISTER:
                (store.dispatch as ThunkDispatch<any, any, any>)(updateRegister(data.message));
                break;
            case SYNC_EVENTS.UPDATE_OUTLET:
                (store.dispatch as ThunkDispatch<any, any, any>)(updateOutlet(data.message));
                break;
            case SYNC_EVENTS.UPDATE_GLOBAL_MODE:
                (store.dispatch as ThunkDispatch<any, any, any>)(changeRegister("global", undefined, false));
                break;
            case SYNC_EVENTS.SYNC_OFFLINE:
                (store.dispatch as ThunkDispatch<any, any, any>)(offline());
                break;
            case SYNC_EVENTS.SYNC_ONLINE:
                (store.dispatch as ThunkDispatch<any, any, any>)(online());
                break;
            case SYNC_EVENTS.SYNC_UNAUTHENTICATED:
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Logging out because of synchronisation");
                }

                (store.dispatch as ThunkDispatch<any, any, any>)(userLogout());
                break;
            case SYNC_EVENTS.FORCE_UPLOAD:
                console.log("Forcing an upload due to sync queue");
                runUploadQueue(true, data.message.reset, true)
                    .then(() => {
                        this.sendMessage(AGENT_EVENTS.DATA_RESPONSE, data.message.returnId);
                    });
                break;
            case SYNC_EVENTS.OUTSTANDING_UPLOADS:
                if(typeof document !== "undefined") {
                    document.dispatchEvent(new CustomEvent(UPLOAD_ERROR_EVENT, {
                        detail: {
                            outstanding: true,
                        },
                    }));
                }

                if(getOption(FORCE_SYNC_FLAG_KEY) !== FORCE_SYNC_FLAG && syncFlagWarning) {
                    syncFlagWarning = true;

                    PopupManager.create({
                        id     : SYNC_FLAG_WARNING_ID,
                        message: "Outstanding uploads are preventing Shopfront from performing a full sync. " +
                            "Your current data is out of date and could cause unexpected errors. Please " +
                            "resolve the outstanding uploads and perform a full synchronisation.",
                        icon   : "exclamation",
                        classes: [ "popup-error" ],
                        timeout: 0,
                    });
                } else {
                    PopupManager.warning(
                        "Unable to perform a full synchronisation as there are outstanding uploads in the queue"
                    );
                }

                break;
            default:
                console.error("Unknown data passed to SyncAgent");
        }

        this.checkSyncStatus();
    }

    protected handleStoreChange(): void {
        const state = store.getState();

        if(state.location.isGlobal && !this.globalMode) {
            this.sendMessage(AGENT_EVENTS.GLOBAL_MODE, true);
            this.globalMode = true;

            return;
        }

        if(state.location.outlet === null) {
            this.outlet = null;
            this.register = null;

            return;
        }

        if(this.outlet !== state.location.outlet.id) {
            this.outlet = state.location.outlet.id;

            if(state.location.register && this.register !== state.location.register.id) {
                this.register = state.location.register.id;
                this.sendMessage(AGENT_EVENTS.REGISTER, this.register);
            } else {
                this.sendMessage(AGENT_EVENTS.OUTLET, this.outlet);
            }
        } else if(state.location.register && this.register !== state.location.register.id) {
            this.register = state.location.register.id;
            this.sendMessage(AGENT_EVENTS.REGISTER, this.register);
        }
    }

    protected handleInstanceChange = (_: "_instance_id", newValue: string | false, oldValue: string | false): void => {
        if(newValue !== oldValue) {
            this.sendMessage(AGENT_EVENTS.INSTANCE_ID, newValue);
        }
    };

    protected checkSyncStatus(): void {
        if(this.syncing && this.page as number > 1) {
            this.showPopup();
        } else {
            this.hidePopup();
        }
    }

    protected showPopup(): void {
        if(this.popup) {
            return;
        }

        PopupManager.create({
            id     : SYNC_AGENT_POPUP_ID,
            message: "Performing a large synchronisation...",
            icon   : <FontAwesomeSolid icon="spinner-third" spin />,
            classes: [ "popup-info" ],
            timeout: 0,
        });

        this.popup = true;
    }

    protected hidePopup(): void {
        if(!this.popup) {
            return;
        }

        PopupManager.remove(SYNC_AGENT_POPUP_ID);

        this.popup = false;
    }

    protected shouldUpdateSaleKeys(key: string, newOptions: Record<string, any>): boolean {
        if(key !== "register_default_sale_keys" && key !== "outlet_default_sale_keys" && key !== "default_sale_keys") {
            return false;
        }

        if(key === "register_default_sale_keys") {
            return newOptions["register_default_sale_keys"] !== getOption("register_default_sale_keys") &&
                newOptions["register_default_sale_keys"] !== "default";
        }

        const register = newOptions["register_default_sale_keys"] || getOption("register_default_sale_keys");

        if(key === "outlet_default_sale_keys") {
            if(register === "default") {
                return newOptions["outlet_default_sale_keys"] !== getOption("outlet_default_sale_keys") &&
                    newOptions["outlet_default_sale_keys"] !== "default";
            } else {
                return false;
            }
        }

        const outlet = newOptions["outlet_default_sale_keys"] || getOption("outlet_default_sale_keys");

        if(key === "default_sale_keys") {
            if(register === "default" && outlet === "default") {
                return newOptions["default_sale_keys"] !== getOption("default_sale_keys") &&
                    newOptions["default_sale_keys"] !== "default";
            } else {
                return false;
            }
        }

        return false;
    }

    public isSyncing(): boolean {
        return this.syncing;
    }

    public performSync(fullSync: boolean): Promise<void> {
        if(fullSync) {
            setOption("lastsync", "");
        }

        return new Promise((res) => {
            this.sendMessage(AGENT_EVENTS.FORCE_SYNC, {
                shouldClearTakings: fullSync,
            }, res);
        });
    }
}
